// P-009: Organization Switcher — thin wrapper over @agion/ui OrgSwitcher
import React from 'react'
import { useTranslation } from 'react-i18next'
import { OrgSwitcher as BaseOrgSwitcher, type OrgSwitcherOrganization } from '@agion/ui'
import { organizationService } from '@/services/organizationService'
import { useAuth } from '@/shared/auth/unified-auth'
import { createLogger } from '@/shared/lib/logger'

const logger = createLogger('OrgSwitcher')

interface OrgSwitcherProps {
  /** Variant for different layout contexts */
  variant?: 'header' | 'sidebar'
  /** Whether sidebar is collapsed (only for sidebar variant) */
  collapsed?: boolean
}

export const OrgSwitcher: React.FC<OrgSwitcherProps> = ({ variant = 'header', collapsed = false }) => {
  const { t } = useTranslation(['common', 'components'])
  const { user } = useAuth()
  const [organizations, setOrganizations] = React.useState<OrgSwitcherOrganization[]>([])
  const [isLoading, setIsLoading] = React.useState(true)

  const currentOrgId = user?.organizationId

  // Load user's organizations on mount
  React.useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setIsLoading(true)
        const orgs = await organizationService.getUserOrganizations()
        setOrganizations(orgs)
        logger.info('Loaded user organizations', { count: orgs.length })
      } catch (err) {
        logger.error('Failed to load organizations', err as Error)
      } finally {
        setIsLoading(false)
      }
    }

    loadOrganizations()
  }, [])

  const handleSwitch = async (orgId: string) => {
    await organizationService.switchOrganization(orgId)
    logger.info('Switched organization', { fromOrg: currentOrgId, toOrg: orgId })
    window.location.reload()
  }

  return (
    <BaseOrgSwitcher
      organizations={organizations}
      currentOrgId={currentOrgId}
      onSwitch={handleSwitch}
      variant={variant}
      collapsed={collapsed}
      isLoading={isLoading}
      labels={{
        selectOrg: t('components:orgSwitcher.selectOrg', { defaultValue: 'Select Organization' }),
        title: t('components:orgSwitcher.title', { defaultValue: 'Organizations' }),
        description: t('components:orgSwitcher.description', { defaultValue: 'Switch between your organizations' }),
        tooltip: t('components:orgSwitcher.tooltip', { defaultValue: 'Switch organization' }),
      }}
    />
  )
}
